import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Sparkles, Send, Check, BookOpen, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function ContactModal({ open, onOpenChange }) {
  const [step, setStep] = useState("form"); // form | result
  const [loading, setLoading] = useState(false);
  const [matched, setMatched] = useState(null);
  const [fallback, setFallback] = useState("");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const reset = () => {
    setStep("form");
    setMatched(null);
    setFallback("");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("All fields are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setMatched(data.matched_article);
      setFallback(data.fallback_message || "");
      setStep("result");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(o); setTimeout(reset, 200); } else onOpenChange(o); }}>
      <DialogContent data-testid="contact-modal" className="max-w-xl bg-charcoal border-border">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-teal" /> Self-serve Help
          </DialogTitle>
          <DialogDescription className="text-white/50">
            We don't operate an open support inbox. Submissions are routed through our self-serve knowledge base for an instant answer.
          </DialogDescription>
        </DialogHeader>

        {step === "form" && (
          <form className="space-y-3 mt-2" onSubmit={submit} data-testid="contact-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 block mb-1.5">Name</label>
                <Input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-charcoal border-border rounded-sm focus-visible:ring-teal"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 block mb-1.5">Email</label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-charcoal border-border rounded-sm focus-visible:ring-teal"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 block mb-1.5">Subject</label>
              <Input
                data-testid="contact-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="bg-charcoal border-border rounded-sm focus-visible:ring-teal"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 block mb-1.5">Message</label>
              <Textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="min-h-[120px] bg-charcoal border-border resize-none rounded-sm focus-visible:ring-teal"
              />
            </div>
            <Button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-teal hover:bg-teal-hover text-white rounded-sm"
            >
              <Send className="h-4 w-4 mr-2" /> {loading ? "Routing…" : "Find an answer"}
            </Button>
          </form>
        )}

        {step === "result" && (
          <div className="mt-2" data-testid="contact-result">
            {matched ? (
              <>
                <div className="sr-pill text-teal mb-3"><Check className="h-3 w-3" /> Matched a KB article</div>
                <h3 className="font-display text-lg text-white tracking-tight">{matched.title}</h3>
                <ScrollArea className="mt-2 max-h-[260px] pr-3">
                  <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{matched.body}</p>
                </ScrollArea>
              </>
            ) : (
              <>
                <div className="sr-pill text-orange-alert mb-3"><Sparkles className="h-3 w-3" /> Logged for review</div>
                <p className="text-sm text-white/75 leading-relaxed">{fallback}</p>
              </>
            )}
            <div className="flex gap-2 mt-5">
              <Button
                data-testid="contact-back"
                variant="outline"
                onClick={() => reset()}
                className="border-border bg-charcoal text-white/70 rounded-sm"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Ask another
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
